import Vue from 'vue'
import { adminRoles, UserRoleEnum } from '@/enums/userRolesEnum'
import { HTTP } from '@/utils/httpCommonUtil';
import { BallPulseLoader } from 'vue-loaders';
import FacilityControl from '@/componentsReusable/FacilityControl.vue'
import TimePickerControl from '@/componentsReusable/TimePickerControl.vue'
import flatPickr from 'vue-flatpickr-component';
import format from 'date-fns/format'
import { convertDateToUTC } from '@/utils/dateUtil';
import { DateTime, Duration } from "luxon";

export default Vue.extend({
  name: 'NewShift',
  data() {
    return {
      cardExpanded: true,
      UserRoleEnum: UserRoleEnum,
      isAuthorized: false,
      facilityId: null,
      facilityName: '',
      facilityTimeZone: '',
      facilityIdErrorMessage: '',
      specialtyId: null,
      specialtyName: '',
      loadingInitialData: false,
      isMondaySelected: false,
      isTuesdaySelected: false,
      isWednesdaySelected: false,
      isThursdaySelected: false,
      isFridaySelected: false,
      isSaturdaySelected: false,
      isSundaySelected: false,
      pendingShifts: [],
      dates: null,
      shiftTypeId: 1 as any,
      flatpickrConfig: {
        showMonths: 2,
        inline: true,
        mode: "multiple",
        locale: {
          'firstDayOfWeek': 1 // start week on Monday
        },
      },
      startTime: "19:00",
      endTime: "07:00",
      incompleteShiftDetails: true,
      savingPendingShifts: false
    }
  },
  components: {
    BallPulseLoader,
    FacilityControl,
    TimePickerControl,
    flatPickr,
  },
  beforeMount: function () {
    this.isAuthorized = this.$store.getters['user/hasRole'](adminRoles);
  },
  filters: {
    dateformat(date: Date) {
      return format(convertDateToUTC(date), 'MMM DD, YYYY - hh:mm A');
    }
  },
  computed: {
    isRecurringDaysValidationMet: function () {
      return !(parseInt(this.shiftTypeId, 10) === 3 &&
        this.isMondaySelected === false &&
        this.isTuesdaySelected === false &&
        this.isWednesdaySelected === false &&
        this.isThursdaySelected === false &&
        this.isFridaySelected === false &&
        this.isSaturdaySelected === false &&
        this.isSundaySelected === false
      );
    },
    isDatesValidationMet: function () {
      if (parseInt(this.shiftTypeId, 10) === 2 || parseInt(this.shiftTypeId, 10) === 3) {
        return this.dates && ((this.dates as any).indexOf('to') !== -1)
      } else {
        return this.dates;
      }
    },
  },
  methods: {
    facilitySelected(facility: any) {
      this.specialtyId = null;
      this.facilityId = facility.id;
      this.facilityName = facility.name;
      this.facilityTimeZone = facility.timeZone;
    },
    toggleDayOfWeek(isSelected: any) {
      this.calculatePendingShifts();
    },
    specialtySelected(specialty: any) {
      this.specialtyId = specialty.specialtyId;
      this.specialtyName = specialty.specialtyName;
      this.calculatePendingShifts();
    },
    datesChange() {
      let self = this;
      setTimeout(function () {
        self.calculatePendingShifts();
      }, 0);
    },
    startTimeSelected(time: any) {
      this.startTime = time;
      this.calculatePendingShifts();
    },
    endTimeSelected(time: any) {
      this.endTime = time;
      this.calculatePendingShifts();
    },
    shiftTypeChange() {
      this.dates = null;
      this.resetRecurringDays();
      this.flatpickrConfig.mode = parseInt(this.shiftTypeId, 10) === 1 ? "multiple" : "range";
      this.calculatePendingShifts();
    },
    resetRecurringDays() {
      this.isMondaySelected = false;
      this.isTuesdaySelected = false;
      this.isWednesdaySelected = false;
      this.isThursdaySelected = false;
      this.isFridaySelected = false;
      this.isSaturdaySelected = false;
      this.isSundaySelected = false;
    },
    reset() {
      this.dates = null;
      this.resetRecurringDays();
      this.facilityId = null;
      this.facilityName = '';
      this.facilityTimeZone = '';
      this.facilityIdErrorMessage = '';
      this.specialtyId = null;
      this.shiftTypeId = 1;
      this.shiftTypeChange();
      this.startTime = "19:00";
      this.endTime = "07:00";
      this.calculatePendingShifts();
    },
    removePendingShift(pendingShift: any) {
      let indexToRemove = null;
      this.pendingShifts.forEach(function (shift: any, index: any) {
        if (pendingShift.id === shift.id) {
          indexToRemove = index;
        }
      });
      if (indexToRemove) {
        this.pendingShifts.splice(indexToRemove, 1);
      }
    },
    calculateDiffWithTimeZone(startTime: DateTime, endTime: DateTime): Duration {
      const startDateRezoned = startTime.setZone(this.facilityTimeZone, { keepLocalTime: true });
      const endDateRezoned = endTime.setZone(this.facilityTimeZone, { keepLocalTime: true });

      return endDateRezoned.diff(startDateRezoned , ['hours', 'minutes']);
    },
    generatePendingShift(startDateString: string, pendingShiftId: any) {
      let startTimeSplit = this.startTime.split(':');
      let endTimeSplit = this.endTime.split(':');
      let startHour = +startTimeSplit[0];
      let endHour = +endTimeSplit[0];
      let startTimeMinutes = +startTimeSplit[1];
      let endTimeMinutes = +endTimeSplit[1];
      if (startDateString) {
        const dateParts = startDateString.split('-');
        let start = DateTime.fromObject({
          year: Number(dateParts[0]),
          month: Number(dateParts[1]),
          day: Number(dateParts[2]),
          hour: Number(startHour),
          minute: Number(startTimeMinutes),
          second: 0,
          millisecond: 0
        });
        let end = DateTime.fromObject({
          year: Number(dateParts[0]),
          month: Number(dateParts[1]),
          day: Number(dateParts[2]),
          hour: Number(endHour),
          minute: Number(endTimeMinutes),
          second: 0,
          millisecond: 0
        });
        if ((startHour > endHour) || (startHour === endHour && startTimeMinutes >= endTimeMinutes)) {
          end = end.plus({ days: 1 });
        }

        const diff = this.calculateDiffWithTimeZone(start, end);

        // this is annoying, but for cost efficiencies sake we have to put back into UTC to make everything else work.
        const startDateTime = new Date(Date.UTC(
          start.year,
          start.month - 1,
          start.day,
          start.hour,
          start.minute,
          start.second,
          start.millisecond
        ));
        const endDateTime = new Date(Date.UTC(
          end.year,
          end.month - 1,
          end.day,
          end.hour,
          end.minute,
          end.second,
          end.millisecond
        ));

        return {
          id: pendingShiftId,
          facilityId: this.facilityId,
          facilityName: this.facilityName,
          specialtyId: this.specialtyId,
          specialtyName: this.specialtyName,
          startDateTime: startDateTime,
          endDateTime: endDateTime,
          hours: diff.hours,
          minutes: diff.minutes,
        };
      }
    },
    calculatePendingShifts() {
      this.pendingShifts = [];

      // First validate that we have all the data we need.
      this.incompleteShiftDetails =
        !(
          Boolean(this.facilityId) &&
          Boolean(this.specialtyId) &&
          this.isDatesValidationMet &&
          this.isRecurringDaysValidationMet &&
          Boolean(this.startTime) &&
          Boolean(this.endTime)
        )
        ;

      // If we have all the data we need, generate the pending shifts.
      if (!this.incompleteShiftDetails) {
        let shifts = [] as any;
        let self = this;

        // If the Shift Type is "Less than 24 hours"
        if (parseInt(this.shiftTypeId, 10) === 1) {
          let startDates = (this.dates as any).split(', ');
          startDates.forEach(function (startDate: any, index: any) {
            shifts.push(self.generatePendingShift(startDate, index));
          });
        }
        // If the Shift Type is "Greater than 24 hours"
        if (parseInt(this.shiftTypeId, 10) === 2) {
          let dates = (this.dates as any).split(' to ');
          let tempStartDateString = dates[0];
          let tempEndDateString = dates[1];
          if (tempStartDateString && tempEndDateString) {
            
            const startDateParts = tempStartDateString.split('-');
            const endDateParts = tempEndDateString.split('-');
            let startTimeSplit = this.startTime.split(':');
            let endTimeSplit = this.endTime.split(':');

            const IsoStartDateTime = DateTime.fromObject(
              { 
                year: Number(startDateParts[0]), 
                month: Number(startDateParts[1]), 
                day: Number(startDateParts[2]), 
                hour: Number(startTimeSplit[0]), 
                minute: Number(startTimeSplit[1]) 
              }
            )

            const IsoEndDateTime = DateTime.fromObject(
              { 
                year: Number(endDateParts[0]), 
                month: Number(endDateParts[1]), 
                day: Number(endDateParts[2]), 
                hour: Number(endTimeSplit[0]), 
                minute: Number(endTimeSplit[1]) 
              }
            )

            let isoStartDate = new Date(Date.UTC(Number(startDateParts[0]), Number(startDateParts[1]) - 1, Number(startDateParts[2]), Number(startTimeSplit[0]), Number(startTimeSplit[1])));
            let isoEndDate = new Date(Date.UTC(Number(endDateParts[0]), Number(endDateParts[1]) - 1, Number(endDateParts[2]), Number(endTimeSplit[0]), Number(endTimeSplit[1])));

            const diff = this.calculateDiffWithTimeZone(IsoStartDateTime, IsoEndDateTime);

            shifts.push(
              {
                id: 0,
                facilityId: self.facilityId,
                facilityName: self.facilityName,
                specialtyId: self.specialtyId,
                specialtyName: self.specialtyName,
                startDateTime: isoStartDate,
                endDateTime: isoEndDate,
                hours: diff.hours,
                minutes: diff.minutes,
              }
            );
          }
        }
        // If the Shift Type is "Range of recurring shifts"
        if (parseInt(this.shiftTypeId, 10) === 3) {
          let dates = (this.dates as any).split(' to ');
          let rangeStartDateString = dates[0];
          let rangeEndDateString = dates[1];
          if (rangeStartDateString && rangeEndDateString) {
            let rangeStartDate = new Date(rangeStartDateString);
            let rangeEndDate = new Date(rangeEndDateString);
            let tempCurrentDate = new Date(rangeStartDate.toString());
            let currentDay;
            let index = 0;
            while (tempCurrentDate <= rangeEndDate) {
              currentDay = tempCurrentDate.getUTCDay();
              if (this.isMondaySelected && currentDay === 1) { shifts.push(this.generatePendingShift(tempCurrentDate.toISOString().substr(0, 10), index)); }
              if (this.isTuesdaySelected && currentDay === 2) { shifts.push(this.generatePendingShift(tempCurrentDate.toISOString().substr(0, 10), index)); }
              if (this.isWednesdaySelected && currentDay === 3) { shifts.push(this.generatePendingShift(tempCurrentDate.toISOString().substr(0, 10), index)); }
              if (this.isThursdaySelected && currentDay === 4) { shifts.push(this.generatePendingShift(tempCurrentDate.toISOString().substr(0, 10), index)); }
              if (this.isFridaySelected && currentDay === 5) { shifts.push(this.generatePendingShift(tempCurrentDate.toISOString().substr(0, 10), index)); }
              if (this.isSaturdaySelected && currentDay === 6) { shifts.push(this.generatePendingShift(tempCurrentDate.toISOString().substr(0, 10), index)); }
              if (this.isSundaySelected && currentDay === 0) { shifts.push(this.generatePendingShift(tempCurrentDate.toISOString().substr(0, 10), index)); }
              index++;
              tempCurrentDate.setUTCDate(tempCurrentDate.getUTCDate() + 1);
            }
          }
        }

        this.pendingShifts = shifts;
      }
    },
    cancelPendingShifts() {
      this.reset();
    },
    async savePendingShifts(stayOnThisPage: boolean = true) {
      this.savingPendingShifts = true;
      const arrayOfPromises = this.pendingShifts.map(this.insertShift);
      try {
        // A way to respond after all of the shift creation promises have returned.
        const results = await Promise.all(arrayOfPromises);
        const successfulSaves = results.filter((result: boolean) => result === true).length;
        if (successfulSaves > 0) {
          let wordShift = successfulSaves > 1 ? ' shifts!' : ' shift!';
          this.$notify({
            group: 'main',
            type: 'success',
            title: 'Success!',
            text: 'You successfully saved ' + successfulSaves + wordShift
          });

          this.dates = null;
          if (!stayOnThisPage) {
            this.$router.push({ name: 'CalendarShifts' });
          }
        } else {
          this.$notify({
            group: 'main',
            type: 'error',
            title: 'Error!',
            text: successfulSaves + " shifts were saved."
          });
        }
      } catch (err) {
        // Should never get in this catch because each save catches itself.
        console.error(err);
        this.$notify({
          group: 'main',
          type: 'error',
          title: 'Error',
          text: 'An unknown error occurred. Zero to all Shifts may have been created.'
        });
      }
      this.savingPendingShifts = false;
    },
    async insertShift(pendingShift: any): Promise<boolean> {
      try {
        const response = await HTTP.post('/api/shifts/', pendingShift);
        
        if (!response.data || !response.data.success) {
          throw new Error("Error saving pending shift.");
        }
        return true;
      } catch (err) {
        if (err.response && err.response.status === 400) {
          this.$notify({
            group: 'main',
            type: 'error',
            title: 'Error!',
            text: err.response.data
          });
          return false;
        }
        console.log(err);
        return false;
      }
    }
  }
})



// WEBPACK FOOTER //
// ./src/components/newShift/newShift.ts